/**
 * useAdminAuth Hook
 * Checks if current user has admin access and handles redirects
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { checkAdminAccess } from '../services/adminService';

interface UseAdminAuthResult {
  isAdmin: boolean;
  isLoading: boolean;
  error: string | null;
}

export const useAdminAuth = (): UseAdminAuthResult => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const verifyAdminAccess = async () => {
      try {
        // Check if user is logged in first
        const token = localStorage.getItem('account-token');
        if (!token) {
          // Not logged in, redirect to login
          navigate('/login', { replace: true });
          return;
        }

        // Check admin access
        const hasAccess = await checkAdminAccess();
        
        if (!hasAccess) {
          // Not admin, redirect to 404 (hide admin route existence)
          navigate('/404', { replace: true });
          return;
        }

        setIsAdmin(true);
        setError(null);
      } catch (err) {
        console.error('Admin auth check failed:', err);
        setError('Failed to verify admin access');
        navigate('/404', { replace: true });
      } finally {
        setIsLoading(false);
      }
    };

    verifyAdminAccess();
  }, [navigate]);

  return { isAdmin, isLoading, error };
};

export default useAdminAuth;

